import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useIntl } from 'react-intl';
import { getBenefitIcon, BenefitIconType } from '../../components/shared/BenefitIcons';
import useScrollToTop from '../../hooks/useScrollToTop';

const StLucia: React.FC = () => {
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id });
  useScrollToTop();

  const benefits: { icon: BenefitIconType; text: string }[] = [
    { icon: 'flight', text: t('citizenship.stLucia.benefits.travel') },
    { icon: 'work', text: t('citizenship.stLucia.benefits.work') },
    { icon: 'security', text: t('citizenship.stLucia.benefits.healthcare') },
    { icon: 'school', text: t('citizenship.stLucia.benefits.quality') },
    { icon: 'timeline', text: t('citizenship.stLucia.benefits.business') },
    { icon: 'family', text: t('citizenship.stLucia.benefits.family') },
  ];

  const requirements = [
    t('citizenship.stLucia.requirements.investment'),
    t('citizenship.stLucia.requirements.criminal'),
    t('citizenship.stLucia.requirements.passport'),
    t('citizenship.stLucia.requirements.financial'),
    t('citizenship.stLucia.requirements.fee'),
    t('citizenship.stLucia.requirements.insurance'),
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5))',
          backgroundColor: '#1976d2',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          height: '50vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Typography variant="h2" component="h1" gutterBottom>
              {t('citizenship.stLucia.hero.title')}
            </Typography>
            <Typography variant="h5" gutterBottom>
              {t('citizenship.stLucia.hero.subtitle')}
            </Typography>
          </motion.div>
        </Container>
      </Box>

      {/* Program Overview */}
      <Container sx={{ py: 8 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Typography variant="h4" gutterBottom align="center">
                {t('citizenship.stLucia.overview.title')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('citizenship.stLucia.overview.description1')}
              </Typography>
              <Typography variant="body1" paragraph align="center">
                {t('citizenship.stLucia.overview.description2')}
              </Typography>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      {/* Benefits Section */}
      <Box sx={{ bgcolor: 'background.paper', py: 8 }}>
        <Container>
          <Typography variant="h3" component="h2" align="center" gutterBottom>
            {t('citizenship.stLucia.benefits.title')}
          </Typography>
          <Grid container spacing={4} sx={{ mt: 2 }}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Box sx={{ color: 'primary.main', mr: 2 }}>
                          {getBenefitIcon(benefit.icon)}
                        </Box>
                        <Typography variant="h6" component="h3">
                          {benefit.text}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Requirements Section */}
      <Container sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom>
          {t('citizenship.stLucia.requirements.title')}
        </Typography>
        <Grid container spacing={4} sx={{ mt: 2 }}>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('citizenship.stLucia.requirements.investmentTitle')}
                  </Typography>
                  <List>
                    {requirements.map((requirement, index) => (
                      <ListItem key={index}>
                        <ListItemText primary={requirement} />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
          <Grid item xs={12} md={6}>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <Card>
                <CardContent>
                  <Typography variant="h5" gutterBottom>
                    {t('citizenship.stLucia.timeline.title')}
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.stLucia.timeline.initial.title')}
                        secondary={t('citizenship.stLucia.timeline.initial.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.stLucia.timeline.residence.title')}
                        secondary={t('citizenship.stLucia.timeline.residence.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.stLucia.timeline.permanent.title')}
                        secondary={t('citizenship.stLucia.timeline.permanent.description')}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary={t('citizenship.stLucia.timeline.citizenship.title')}
                        secondary={t('citizenship.stLucia.timeline.citizenship.description')}
                      />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default StLucia; 